// use moment here for now because of it relies on an old calculateTargetDatesFromOperator helper that uses moment
import moment from 'moment/moment';

import { STATUS } from './filters/options/status';
import { SHIP_DATES } from './filters/options/ship-date';
import { CREATE_DATES } from './filters/options/create-date';
import { dateFilterOperators } from '../../../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { calculateTargetDatesFromOperator } from '../../../../services/filtering/api-param-map-service';
import { OptionFormTypes } from '../../../../services/filtering/filter-form-render-service/option-form-types';

const FREIGHT_GRID_TYPE = 'freight';

export type AppliedFilter = {
  filterById: string;
  filterValue: string;
  criterionArgumentValue: string | { startRangeDate: string; endRangeDate: string };
  criterionOperatorValue: string;
  type: string;
};

export type FreightFilterApiParams = {
  filter?: {
    collectionFilters?: Array<{
      column: string;
      values: string[];
      isIn: boolean;
    }>;
    dateFilters?: Array<{
      column: string;
      minDateUtc?: string;
      maxDateUtc?: string;
    }>;
  };
};

const mapDateFilter = (
  column: string,
  criterionOperatorValue: string,
  criterionArgumentValue: string | { startRangeDate: string; endRangeDate: string },
) => {
  if (criterionOperatorValue === dateFilterOperators.dateCustomRange) {
    const rangeValue = criterionArgumentValue as { startRangeDate: string; endRangeDate: string };
    return {
      column,
      minDateUtc: moment(rangeValue.startRangeDate).format(),
      maxDateUtc: moment(rangeValue.endRangeDate).add(1, 'days').format(),
    };
  }
  if (criterionOperatorValue === dateFilterOperators.dateOnOrAfter) {
    return {
      column,
      minDateUtc: moment(criterionArgumentValue as string).format(),
    };
  }
  if (criterionOperatorValue === dateFilterOperators.dateOnOrBefore) {
    return {
      column,
      maxDateUtc: moment(criterionArgumentValue as string)
        .add(1, 'days')
        .format(),
    };
  }
  // For preset operators (today, yesterday, last week, etc.)
  const targetDates = calculateTargetDatesFromOperator(criterionOperatorValue, FREIGHT_GRID_TYPE);
  const startDate = targetDates.greaterThanOrEqualToDate;
  const endDate = targetDates.lessThanDate;

  const formatDate = (date: typeof startDate) => {
    if (!date) return undefined;
    if (typeof date === 'string') return moment(date).format();
    return date.format();
  };

  return {
    column,
    minDateUtc: formatDate(startDate),
    maxDateUtc: formatDate(endDate),
  };
};

export const mapFreightFiltersToApiParams = (filters: AppliedFilter[]): FreightFilterApiParams => {
  const collectionFilters: Array<{ column: string; values: string[]; isIn: boolean }> = [];
  const dateFilters: Array<{ column: string; minDateUtc?: string; maxDateUtc?: string }> = [];

  for (const filter of filters) {
    const { filterValue, criterionArgumentValue, criterionOperatorValue, type } = filter;

    if (filterValue === STATUS) {
      const existingStatusFilter = collectionFilters.find((f) => f.column === 'Status');
      if (existingStatusFilter) {
        existingStatusFilter.values.push(criterionArgumentValue as string);
      } else {
        collectionFilters.push({
          column: 'Status',
          values: [criterionArgumentValue as string],
          isIn: true,
        });
      }
      continue;
    }

    if (filterValue === SHIP_DATES && type === OptionFormTypes.TYPE_DATE_COMPARISON_SHIPMENTS) {
      dateFilters.push(mapDateFilter('ShipDate', criterionOperatorValue, criterionArgumentValue));
      continue;
    }

    if (filterValue === CREATE_DATES && type === OptionFormTypes.TYPE_DATE_COMPARISON_SHIPMENTS) {
      dateFilters.push(mapDateFilter('CreateDate', criterionOperatorValue, criterionArgumentValue));
      continue;
    }
  }

  const result: FreightFilterApiParams = {};

  if (collectionFilters.length > 0 || dateFilters.length > 0) {
    result.filter = {};
    if (collectionFilters.length > 0) {
      result.filter.collectionFilters = collectionFilters;
    }
    if (dateFilters.length > 0) {
      result.filter.dateFilters = dateFilters;
    }
  }

  return result;
};
